import { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Input } from './ui/input';
import { Calendar as CalendarIcon } from 'lucide-react';

// Utility function to detect iOS/mobile devices with comprehensive error handling
const isMobileOrIOS = () => {
  try {
    // Server-side rendering check
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return false;
    }
    
    const userAgent = window.navigator?.userAgent?.toLowerCase() || '';
    
    // Specific iOS detection with improved regex
    const isIOS = /ipad|iphone|ipod/.test(userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPad Pro detection
    
    // General mobile detection
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    
    // Touch device detection with proper error handling
    let isTouchDevice = false;
    try {
      isTouchDevice = ('ontouchstart' in window) || 
                     (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) || 
                     (navigator.msMaxTouchPoints && navigator.msMaxTouchPoints > 0);
    } catch (touchError) {
      console.warn('Touch detection failed:', touchError);
    }
    
    // Screen width check with fallback
    let screenCheck = false;
    try {
      screenCheck = window.innerWidth <= 1024;
    } catch (screenError) {
      // Screen width check failed, use fallback
      screenCheck = window.screen?.width <= 1024 || false;
    }
    
    const result = isMobile || isIOS || (isTouchDevice && screenCheck);
    
    // Debug logging for troubleshooting (remove in production)
    if (isIOS) {
      log('MobileFriendlyDatePicker: iOS device detected', {
        userAgent: userAgent.substring(0, 50),
        isIOS,
        isMobile,
        isTouchDevice,
        screenCheck,
        result
      });
    }
    
    return result;
  } catch (error) {
    console.error('MobileFriendlyDatePicker: Device detection error:', error);
    return false; // Default to desktop if detection fails completely
  }
};

const isDev = process.env.NODE_ENV === 'development';
const log = (...args) => isDev && log(...args);

const MobileFriendlyDatePicker = ({ 
  selected, 
  onSelect, 
  placeholder = "Pick a date",
  className = "",
  disabled = false 
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Memoize today's date to avoid recalculating on every render
  const todayString = useMemo(() => {
    try {
      const today = new Date();
      // Validate date object
      if (isNaN(today.getTime())) {
        throw new Error('Invalid date object created');
      }
      return today.toISOString().split('T')[0];
    } catch (error) {
      console.error('MobileFriendlyDatePicker: Date formatting error:', error);
      return ''; // Return empty string as fallback
    }
  }, []);

  // Format date for HTML5 date input (YYYY-MM-DD) with error handling
  const formatDateForInput = (date) => {
    try {
      if (!date) return '';
      
      // Handle both Date objects and date strings
      const dateObj = date instanceof Date ? date : new Date(date);
      
      // Check if date is valid
      if (isNaN(dateObj.getTime())) {
        console.warn('Invalid date provided to formatDateForInput:', date);
        return '';
      }
      
      return dateObj.toISOString().split('T')[0];
    } catch (error) {
      console.warn('Date formatting error:', error);
      return '';
    }
  };

  useEffect(() => {
    try {
      const mobileDetected = isMobileOrIOS();
      setIsMobile(mobileDetected);
      setHasError(false); // Reset error state if successful
    } catch (error) {
      console.error('MobileFriendlyDatePicker: Mobile detection error:', error);
      setIsMobile(false);
      setHasError(true);
    }
  }, []);

  // Error boundary fallback - render simple date input if component fails
  if (hasError) {
    console.warn('MobileFriendlyDatePicker: Rendering fallback due to errors');
    return (
      <input
        type="date"
        value={selected ? formatDateForInput(selected) : ''}
        onChange={(e) => {
          try {
            const date = e.target.value ? new Date(e.target.value) : null;
            if (onSelect) onSelect(date);
          } catch (err) {
            console.error('Fallback date picker error:', err);
          }
        }}
        disabled={disabled}
        className={`w-full p-2 border border-gray-300 rounded ${className}`}
        min={todayString}
      />
    );
  }

  // Parse date from HTML5 date input with validation
  const parseDateFromInput = (dateString) => {
    try {
      if (!dateString || typeof dateString !== 'string') return null;
      
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date string provided:', dateString);
        return null;
      }
      
      return date;
    } catch (error) {
      console.warn('Date parsing error:', error);
      return null;
    }
  };

  // Handle native date input change with error handling
  const handleNativeDateChange = (e) => {
    try {
      const dateString = e.target?.value;
      const date = parseDateFromInput(dateString);
      
      if (onSelect && typeof onSelect === 'function') {
        onSelect(date);
      }
    } catch (error) {
      console.warn('Date change handler error:', error);
    }
  };

  // Safe date display helper
  const getDateDisplayText = (date) => {
    try {
      if (!date) return placeholder;
      
      const dateObj = date instanceof Date ? date : new Date(date);
      
      // Check if date is valid
      if (isNaN(dateObj.getTime())) {
        return placeholder;
      }
      
      return dateObj.toDateString();
    } catch (error) {
      console.warn('Date display error:', error);
      return placeholder;
    }
  };

  // Safe date comparison for disabling past dates
  const isDateDisabled = (date) => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const compareDate = new Date(date);
      compareDate.setHours(0, 0, 0, 0);
      
      return compareDate < today;
    } catch (error) {
      console.warn('Date comparison error:', error);
      return false; // Don't disable if comparison fails
    }
  };

  // Mobile/iOS: Use native HTML5 date input
  if (isMobile) {
    return (
      <div className={`relative ${className}`}>
        <Input
          type="date"
          value={formatDateForInput(selected)}
          onChange={handleNativeDateChange}
          disabled={disabled}
          className="w-full"
          min={todayString} // Prevent selecting past dates
        />
      </div>
    );
  }

  // Desktop: Use original popover calendar
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={`w-full justify-start text-left font-normal ${
            !selected && "text-muted-foreground"
          } ${className}`}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {getDateDisplayText(selected)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={onSelect}
          initialFocus
          disabled={isDateDisabled}
        />
      </PopoverContent>
    </Popover>
  );
};

export default MobileFriendlyDatePicker;